import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Logo from '../components/Logo';
import Avatar from '../components/Avatar';
import { DEFAULT_CATEGORIES, CATEGORY_COLORS } from '../utils/categories';
import { User, Gear, Tag, Sparkle, ChatText, CaretLeft, SignOut, Check, X, PencilSimple, Lock, Trash, Plus, Bug, Lightbulb, ChatCircleDots, Trophy, Star, ChartBar, ArrowsClockwise, ShareNetwork, DownloadSimple, CalendarBlank } from '@phosphor-icons/react';

const tabs = [
  {
    key: 'profile',
    label: 'Perfil',
    icon: <User size={16} />,
  },
  {
    key: 'settings',
    label: 'Configuracoes',
    icon: <Gear size={16} />,
  },
  {
    key: 'categories',
    label: 'Categorias',
    icon: <Tag size={16} />,
  },
  {
    key: 'plans',
    label: 'Planos',
    icon: <Sparkle size={16} />,
  },
  {
    key: 'achievements',
    label: 'Conquistas',
    icon: <Trophy size={16} />,
  },
  {
    key: 'feedback',
    label: 'Feedback',
    icon: <ChatText size={16} />,
  },
];

function ProfileTab({ user, onUpdateName }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim() || name.trim() === user?.name) {
      setEditing(false);
      setName(user?.name || '');
      return;
    }
    setSaving(true);
    try {
      await onUpdateName(name.trim());
      setEditing(false);
    } catch {
      // erro silencioso
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 mb-1">Perfil</h2>
      <p className="text-sm text-slate-400 mb-6">Informacoes da sua conta</p>

      <div className="space-y-5">
        <div className="flex items-center gap-4 mb-8">
          <Avatar user={user} size="lg" />
          <div>
            <p className="text-base font-semibold text-slate-800">{user?.name}</p>
            <p className="text-sm text-slate-400">{user?.email}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-500 mb-1">Nome</label>
          <div className="relative flex items-center h-10 px-4 bg-slate-50 border border-slate-200 rounded-xl">
            {editing ? (
              <>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  disabled={saving}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') { setEditing(false); setName(user?.name || ''); }
                  }}
                  className="flex-1 bg-transparent text-sm text-slate-700 outline-none min-w-0 pr-14"
                />
                <div className="absolute right-2 flex items-center gap-0.5">
                  {saving ? (
                    <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        className="p-1.5 rounded-lg text-emerald-500 hover:bg-slate-200/60 transition outline-none"
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => { setEditing(false); setName(user?.name || ''); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200/60 transition outline-none"
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-slate-700">{user?.name}</span>
                <button
                  onClick={() => setEditing(true)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200/60 transition outline-none"
                >
                  <PencilSimple size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-500 mb-1">Email</label>
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
            <span className="text-sm text-slate-700">{user?.email}</span>
            <Lock size={16} className="text-slate-300" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-500 mb-1">Senha</label>
          <ChangePasswordForm />
        </div>
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      return setError('Nova senha deve ter no minimo 6 caracteres');
    }
    if (newPassword !== confirmPassword) {
      return setError('As senhas nao coincidem');
    }

    setLoading(true);
    try {
      await api.put('/auth/password', { currentPassword, newPassword });
      setSuccess('Senha alterada com sucesso');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {!open ? (
        <button
          onClick={() => { setOpen(true); setError(''); setSuccess(''); }}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition text-left"
        >
          <span className="text-sm text-slate-700">••••••••</span>
          <PencilSimple size={16} className="text-slate-400" />
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          {error && <p className="text-xs text-red-500">{error}</p>}
          {success && <p className="text-xs text-emerald-600">{success}</p>}
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoFocus
            placeholder="Senha atual"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
          />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            placeholder="Nova senha"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
          />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder="Confirmar nova senha"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setError(''); setSuccess(''); }}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 transition text-sm font-medium shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {loading ? 'Alterando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const currencies = [
  { code: 'BRL', symbol: 'R$', label: 'Real' },
  { code: 'USD', symbol: '$', label: 'Dolar' },
  { code: 'EUR', symbol: '\u20ac', label: 'Euro' },
];

const themes = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
  { value: 'system', label: 'Sistema' },
];

function SettingsTab({ user, onUpdateSettings, onClearExpenses }) {
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  async function handleChange(key, value) {
    setSaving(key);
    setSaved(null);
    try {
      await onUpdateSettings({ [key]: value });
      setSaved(key);
      setTimeout(() => setSaved(null), 1500);
    } catch {
      setSaved(null);
    } finally {
      setSaving(null);
    }
  }

  async function handleClear() {
    setClearing(true);
    try {
      await onClearExpenses();
      setConfirmClear(false);
    } catch {
      setConfirmClear(false);
    } finally {
      setClearing(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 mb-1">Configuracoes</h2>
      <p className="text-sm text-slate-400 mb-6">Preferencias da sua conta</p>

      <div className="space-y-1">
        {/* Tema */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 border-b border-slate-100 gap-2 sm:gap-0">
          <div>
            <p className="text-sm font-medium text-slate-700">Tema</p>
            <p className="text-xs text-slate-400">Aparencia da interface</p>
          </div>
          <div className="flex items-center gap-2">
            {saving === 'theme' && (
              <svg className="w-3.5 h-3.5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {saved === 'theme' && (
              <Check size={14} weight="bold" className="text-emerald-500" />
            )}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {themes.map(t => (
                <button
                  key={t.value}
                  onClick={() => handleChange('theme', t.value)}
                  disabled={saving === 'theme'}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                    user?.theme === t.value
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Moeda */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 border-b border-slate-100 gap-2 sm:gap-0">
          <div>
            <p className="text-sm font-medium text-slate-700">Moeda</p>
            <p className="text-xs text-slate-400">Moeda usada nos valores</p>
          </div>
          <div className="flex items-center gap-2">
            {saving === 'currency' && (
              <svg className="w-3.5 h-3.5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {saved === 'currency' && (
              <Check size={14} weight="bold" className="text-emerald-500" />
            )}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {currencies.map(c => (
                <button
                  key={c.code}
                  onClick={() => handleChange('currency', c.code)}
                  disabled={saving === 'currency'}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                    user?.currency === c.code
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {c.symbol} {c.code}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Resumo mensal */}
        <div className="flex items-center justify-between py-4 border-b border-slate-100">
          <div>
            <p className="text-sm font-medium text-slate-700">Resumo mensal</p>
            <p className="text-xs text-slate-400">Receber email com resumo dos gastos</p>
          </div>
          <button
            onClick={() => handleChange('monthlyReport', !user?.monthlyReport)}
            className={`relative w-10 h-5 rounded-full transition ${user?.monthlyReport ? 'bg-blue-600' : 'bg-slate-200'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${user?.monthlyReport ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Limpar dados */}
        <div className="flex items-center justify-between py-4">
          <div>
            <p className="text-sm font-medium text-red-600">Limpar dados</p>
            <p className="text-xs text-slate-400">Remove todas as despesas da conta</p>
          </div>
          {confirmClear ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleClear}
                disabled={clearing}
                className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition disabled:opacity-50"
              >
                {clearing ? 'Limpando...' : 'Confirmar'}
              </button>
              <button
                onClick={() => setConfirmClear(false)}
                disabled={clearing}
                className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmClear(true)}
              className="px-3 py-1.5 bg-red-50 text-red-500 rounded-lg text-xs font-medium hover:bg-red-100 transition"
            >
              Limpar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(CATEGORY_COLORS[0]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    setError('');
    try {
      const { data } = await api.post('/categories', { name: newName.trim(), color: newColor });
      setCategories(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
      setNewColor(CATEGORY_COLORS[Math.floor(Math.random() * CATEGORY_COLORS.length)]);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar categoria');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await api.delete(`/categories/${id}`);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch {
      setError('Erro ao remover categoria');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 mb-1">Suas categorias</h2>
      <p className="text-sm text-slate-400 mb-6">Organize suas despesas por categoria</p>

      <form onSubmit={handleAdd} className="flex flex-wrap sm:flex-nowrap gap-2 mb-6">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition"
          >
            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: newColor }} />
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200/60 p-3 z-50 w-48">
              <div className="grid grid-cols-5 gap-3">
                {CATEGORY_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => { setNewColor(color); setShowColorPicker(false); }}
                    className={`w-7 h-7 rounded-full transition hover:scale-110 ${newColor === color ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <input
          type="text"
          value={newName}
          onChange={(e) => { setNewName(e.target.value); setError(''); }}
          placeholder="Nova categoria..."
          className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
        />
        <button
          type="submit"
          disabled={adding || !newName.trim()}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {adding ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <Plus size={16} weight="bold" />
          )}
          Adicionar
        </button>
      </form>

      {error && (
        <p className="text-sm text-red-500 mb-4">{error}</p>
      )}

      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 420px)' }}>
      <div className="mb-6">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Padrao</p>
        <div className="space-y-1">
          {DEFAULT_CATEGORIES.map(c => (
            <div key={c.name} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
              <span className="text-sm font-medium text-slate-500">{c.name}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Personalizadas</p>
      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="w-5 h-5 text-slate-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : categories.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">Nenhuma categoria personalizada</p>
      ) : (
        <div className="space-y-1">
          {categories.map(cat => (
            <div
              key={cat.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-slate-50 transition group"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                <span className="text-sm font-medium text-slate-700">{cat.name}</span>
              </div>
              <button
                onClick={() => handleDelete(cat.id)}
                disabled={deletingId === cat.id}
                className="p-1.5 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-50"
              >
                {deletingId === cat.id ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Trash size={16} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <Check size={16} weight="bold" className="text-emerald-500 shrink-0" />
  );
}

function XIcon() {
  return (
    <X size={16} weight="bold" className="text-slate-300 shrink-0" />
  );
}

function PlansTab({ user, onRefreshUser }) {
  const isPro = user?.plan === 'pro';
  const isCanceled = isPro && user?.subscriptionStatus === 'canceled';
  const endsAt = user?.subscriptionEndsAt ? new Date(user.subscriptionEndsAt) : null;
  const renewsAt = user?.subscriptionRenewsAt ? new Date(user.subscriptionRenewsAt) : null;
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');
  const portal = searchParams.get('portal');

  // Recarregar dados do usuario ao voltar do Stripe (checkout ou portal) — apenas 1 vez
  const refreshedRef = useRef(false);
  useEffect(() => {
    if ((success || portal) && onRefreshUser && !refreshedRef.current) {
      refreshedRef.current = true;
      onRefreshUser();
    }
  }, [success, portal, onRefreshUser]);

  const freeFeatures = [
    { text: 'Ate 10 despesas por mes', included: true },
    { text: 'Ate 1 categoria personalizada', included: true },
    { text: 'Ate 2 despesas recorrentes', included: true },
    { text: 'Exportar CSV', included: true },
    { text: 'Despesas parceladas', included: false },
    { text: 'Notas nas despesas', included: false },
    { text: 'Exportar PDF', included: false },
    { text: 'Compartilhar relatorio', included: false },
    { text: 'Resumo anual', included: false },
    { text: 'E-mail de relatorio mensal', included: false },
  ];

  const proFeatures = [
    { text: 'Despesas ilimitadas', included: true },
    { text: 'Categorias ilimitadas', included: true },
    { text: 'Recorrencias ilimitadas', included: true },
    { text: 'Exportar CSV', included: true },
    { text: 'Despesas parceladas', included: true },
    { text: 'Notas nas despesas', included: true },
    { text: 'Exportar PDF', included: true },
    { text: 'Compartilhar relatorio', included: true },
    { text: 'Resumo anual', included: true },
    { text: 'E-mail de relatorio mensal', included: true },
  ];

  async function handleUpgrade() {
    setLoading(true);
    try {
      const { data } = await api.post('/stripe/checkout');
      window.location.href = data.url;
    } catch {
      setLoading(false);
    }
  }

  async function handleManage() {
    setLoading(true);
    try {
      const { data } = await api.post('/stripe/portal');
      window.location.href = data.url;
    } catch {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 mb-1">Planos</h2>
      <p className="text-sm text-slate-400 mb-6">Gerencie sua assinatura</p>

      {success && (
        <div className="mb-4 p-3 bg-emerald-100 border-2 border-emerald-300 rounded-xl text-sm text-emerald-800 font-medium">
          Assinatura ativada com sucesso! Aproveite o plano Pro.
        </div>
      )}
      {canceled && (
        <div className="mb-4 p-3 bg-amber-100 border-2 border-amber-300 rounded-xl text-sm text-amber-800 font-medium">
          Pagamento cancelado. Voce continua no plano gratuito.
        </div>
      )}

      {isPro && !isCanceled && renewsAt && (
        <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Plano Pro ativo</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Proxima renovacao em {renewsAt.toLocaleDateString('pt-BR')}</p>
          </div>
          <button
            onClick={handleManage}
            disabled={loading}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition"
          >
            Gerenciar
          </button>
        </div>
      )}

      {isCanceled && endsAt && (
        <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Plano cancelado</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Seu acesso Pro continua ate {endsAt.toLocaleDateString('pt-BR')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Plano Free */}
        <div className={`rounded-2xl p-5 relative ${!isPro ? 'border-2 border-blue-500' : 'border border-slate-200'}`}>
          {!isPro && (
            <div className="absolute -top-2.5 left-4">
              <span className="text-[11px] font-semibold text-white bg-blue-500 px-2.5 py-0.5 rounded-full">Atual</span>
            </div>
          )}
          <h3 className="text-base font-bold text-slate-800 mt-1">Gratuito</h3>
          <p className="text-2xl font-bold text-slate-800 mt-2">R$ 0<span className="text-sm font-normal text-slate-400">/mes</span></p>
          <ul className="mt-4 space-y-2">
            {freeFeatures.map((f, i) => (
              <li key={i} className={`flex items-center gap-2 text-sm ${f.included ? 'text-slate-600' : 'text-slate-400'}`}>
                {f.included ? <CheckIcon /> : <XIcon />}
                {f.text}
              </li>
            ))}
          </ul>
        </div>

        {/* Plano Pro */}
        <div className={`rounded-2xl p-5 relative ${isPro ? 'border-2 border-blue-500' : 'border border-slate-200'}`}>
          {isPro && (
            <div className="absolute -top-2.5 left-4">
              <span className={`text-[11px] font-semibold text-white px-2.5 py-0.5 rounded-full ${isCanceled ? 'bg-amber-500' : 'bg-blue-500'}`}>
                {isCanceled ? 'Cancelado' : 'Atual'}
              </span>
            </div>
          )}
          <h3 className="text-base font-bold text-slate-800 mt-1">Pro</h3>
          <p className="text-2xl font-bold text-slate-800 mt-2">R$ 4,90<span className="text-sm font-normal text-slate-400">/mes</span></p>
          <ul className="mt-4 space-y-2">
            {proFeatures.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckIcon />
                {f.text}
              </li>
            ))}
          </ul>
          {!isPro ? (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full mt-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : null}
              Assinar Pro
            </button>
          ) : (
            <button
              onClick={handleManage}
              disabled={loading}
              className="w-full mt-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition disabled:opacity-50"
            >
              Gerenciar assinatura
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ACHIEVEMENT_ICONS = {
  star: Star,
  tag: Tag,
  'arrows-clockwise': ArrowsClockwise,
  'chart-bar': ChartBar,
  trophy: Trophy,
  download: DownloadSimple,
  share: ShareNetwork,
  sparkle: Sparkle,
  calendar: CalendarBlank,
};

function AchievementsTab() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/achievements').then(({ data }) => setAchievements(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const unlocked = achievements.filter(a => a.unlocked);
  const locked = achievements.filter(a => !a.unlocked);

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">Conquistas</h2>
      <p className="text-sm text-slate-400 mb-6">
        {unlocked.length} de {achievements.length} desbloqueadas
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="w-5 h-5 text-slate-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <div className="space-y-6">
          {unlocked.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Desbloqueadas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {unlocked.map((a) => {
                  const Icon = ACHIEVEMENT_ICONS[a.icon] || Trophy;
                  return (
                    <div key={a.key} className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                      <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{a.name}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">{a.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {locked.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Bloqueadas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {locked.map((a) => {
                  const Icon = ACHIEVEMENT_ICONS[a.icon] || Trophy;
                  return (
                    <div key={a.key} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl opacity-50">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{a.name}</p>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500">???</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FeedbackIcon({ type, className = 'w-4 h-4' }) {
  const size = className.includes('3.5') ? 14 : 16;
  if (type === 'bug') return <Bug size={size} className={className} />;
  if (type === 'suggestion') return <Lightbulb size={size} className={className} />;
  return <ChatCircleDots size={size} className={className} />;
}

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug' },
  { value: 'suggestion', label: 'Sugestão' },
  { value: 'other', label: 'Outro' },
];

function FeedbackTab() {
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    api.get('/feedback').then(({ data }) => setFeedbacks(data)).catch(() => {}).finally(() => setLoadingList(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    setError('');
    setSent(false);
    try {
      const { data } = await api.post('/feedback', { type, message });
      setFeedbacks(prev => [data, ...prev]);
      setMessage('');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar feedback');
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">Feedback</h2>
      <p className="text-sm text-slate-400 mb-6">Encontrou um bug ou tem uma sugestao? Nos conte!</p>

      <form onSubmit={handleSubmit} className="space-y-4 mb-8">
        {/* Tipo */}
        <div className="flex gap-2">
          {FEEDBACK_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setType(t.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                type === t.value
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-800'
                  : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <FeedbackIcon type={t.value} className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Mensagem */}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={type === 'bug' ? 'Descreva o problema que encontrou...' : type === 'suggestion' ? 'Qual sua sugestão para melhorar o DespEasy?' : 'Escreva sua mensagem...'}
          rows={4}
          maxLength={1000}
          className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 transition resize-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{message.length}/1000</span>
          <button
            type="submit"
            disabled={sending || message.trim().length < 10}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : null}
            Enviar
          </button>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        {sent && <p className="text-xs text-emerald-500">Feedback enviado com sucesso! Obrigado.</p>}
      </form>

      {/* Historico */}
      {feedbacks.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Seus feedbacks</h3>
          <div className="space-y-2">
            {feedbacks.map((f) => (
              <div key={f.id} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 border border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1.5">
                  <FeedbackIcon type={f.type} className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {f.type === 'bug' ? 'Bug' : f.type === 'suggestion' ? 'Sugestão' : 'Outro'}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {new Date(f.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">{f.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loadingList && feedbacks.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">Nenhum feedback enviado ainda.</p>
      )}
    </div>
  );
}

export default function Account() {
  const { user, logout, updateUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

  async function handleUpdateName(newName) {
    const { data } = await api.put('/auth/profile', { name: newName });
    updateUser(data.user);
  }

  async function handleUpdateSettings(settings) {
    const { data } = await api.put('/auth/settings', settings);
    updateUser(data.user);
  }

  async function handleClearExpenses() {
    await api.delete('/auth/expenses');
  }

  const ActiveComponent = {
    profile: ProfileTab,
    settings: SettingsTab,
    categories: CategoriesTab,
    plans: PlansTab,
    achievements: AchievementsTab,
    feedback: FeedbackTab,
  }[activeTab];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo linkTo="/dashboard" />
          <Link
            to="/dashboard"
            className="text-sm text-slate-400 hover:text-slate-600 transition flex items-center gap-1.5"
          >
            <CaretLeft size={16} weight="bold" />
            <span className="hidden sm:inline">Voltar ao dashboard</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row gap-8">
          {/* Nav lateral / horizontal no mobile */}
          <nav className="sm:w-48 shrink-0">
            <div className="flex sm:flex-col gap-1 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 sm:gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap shrink-0 sm:shrink sm:w-full ${
                    activeTab === tab.key
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="hidden sm:block mt-6 pt-6 border-t border-slate-200/60">
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition"
              >
                <SignOut size={16} />
                Sair da conta
              </button>
            </div>
          </nav>

          {/* Conteudo */}
          <div className="flex-1 min-w-0">
            <ActiveComponent user={user} onUpdateName={handleUpdateName} onUpdateSettings={handleUpdateSettings} onClearExpenses={handleClearExpenses} onRefreshUser={refreshUser} />
          </div>
        </div>
      </main>
    </div>
  );
}
